import { demoAgents, demoOverview } from "@/lib/demo-data";
import { apiRequest } from "@/lib/auth-api";
import type {
    Agent,
    ApiEnvelope,
    GeneratedCase,
    PageTestSource,
    QualityAnalysisSaveInput,
    QualityAnalysisTask,
    QualityOverview,
    TestRun,
} from "@/lib/qa-types";

const apiBaseUrl = process.env.NEXT_PUBLIC_API_BASE_URL ?? "/api";

async function getData<T>(path: string, fallback: T): Promise<{ data: T; source: "live" | "demo" }> {
    try {
        const response = await fetch(`${apiBaseUrl}${path}`, {
            signal: AbortSignal.timeout(1200),
            headers: { Accept: "application/json" },
        });
        if (!response.ok) return { data: fallback, source: "demo" };
        const envelope = (await response.json()) as ApiEnvelope<T>;
        return { data: envelope.data, source: "live" };
    } catch {
        return { data: fallback, source: "demo" };
    }
}

export function loadOverview() {
    return getData<QualityOverview>("/qa/overview", demoOverview);
}

export function loadAgents() {
    return getData<Agent[]>("/qa/agents", demoAgents);
}

export async function generatePageCases(source: PageTestSource): Promise<GeneratedCase[]> {
    const result = await apiRequest<{ cases: GeneratedCase[] }>("/qa/cases/generate", {
        method: "POST",
        body: JSON.stringify({
            source_type: "WEB_PAGE",
            source_name: source.title,
            source_url: source.target_url,
            requirement: source.requirement,
            characteristics: source.characteristics,
        }),
    });
    return result.cases;
}

export function createPageTestRun(source: PageTestSource, caseIds: string[]): Promise<TestRun> {
    return apiRequest<TestRun>("/qa/runs/create", {
        method: "POST",
        body: JSON.stringify({
            case_ids: caseIds,
            environment: "uat",
            priority: "P0",
            required_labels: ["chrome", "uat"],
            source_url: source.target_url,
            requirement: source.requirement,
        }),
    });
}

export function completePageTestRun(runId: string): Promise<TestRun> {
    return apiRequest<TestRun>("/qa/runs/complete-demo", {
        method: "POST",
        body: JSON.stringify({ run_id: runId, outcome: "healed" }),
    });
}

export function loadLatestQualityAnalysis(): Promise<QualityAnalysisTask | null> {
    return apiRequest<QualityAnalysisTask | null>("/qa/analyses/latest", { method: "GET" });
}

export function saveQualityAnalysis(csrfToken: string, input: QualityAnalysisSaveInput): Promise<QualityAnalysisTask> {
    return apiRequest<QualityAnalysisTask>("/qa/analyses/save", {
        method: "POST",
        headers: { "X-CSRF-Token": csrfToken },
        body: JSON.stringify(input),
    });
}
